/**
 * The photo shelf — photos lead every section (D-028), as a hand (D-032).
 *
 * From the design authority's walkthrough, 2026-08-30: *"This survey is
 * photo-heavy by nature, but photos are attached fields rather than the lead
 * of each section. The camera should be the first thing the thumb reaches."*
 *
 * So every section screen opens with this: a header row — the eyebrow, the
 * count, and the amber add pill — and under it the section's cards, one per
 * recommended shot and one per extra. The pill lives in the HEADER, outside
 * the cards, so no number of photos can push it off screen: adding never
 * requires scrolling (brief §3).
 *
 * ── The hand (D-032) ────────────────────────────────────────────────────────
 * 2026-09-01, the design authority handed over a card-fan carousel for *"the
 * photo component at the top of each survey screen."* The cards are now laid
 * out by `PhotoFan` — a fanned hand with one card in front, walked by the
 * arrows, a swipe, or a tap on a peeking card — instead of the snap scroller.
 * This component still decides everything: which cards exist (`shelfModel`),
 * and which is FRONTED, by the rules in `src/ui/state/photoFan.ts`: a
 * section opens on its middle card; a photo just taken comes to the front,
 * and so does a shot whose photo just landed; a deleted front hands over to
 * its neighbour. The front is derived during render, not in an effect, so
 * the fan sees a new photo already fronted and it rises into the hand.
 *
 * ── What it decides: nothing about the record ───────────────────────────────
 * The arrangement is `shelfModel` (src/ui/state/photoShelf.ts), which reads
 * the same `photoCoversShot` the validation engine and the Survey Map read.
 * Capture goes through `usePhotoCapture`, the one capture path. One tap on
 * the pill opens the OS's own picker (D-030 R10).
 *
 * ── Tapping a card ──────────────────────────────────────────────────────────
 * A card with a photo opens the viewer (full screen / label / retake /
 * delete) — the FRONT one directly, and a peeking one in the SAME tap that
 * fronts it (round 8.1 task 5, `FanCard.actsWhenPeeking`; the fan owns why).
 * The front card without a photo — a recommended shot still to take — opens
 * the picker scoped to that shot, so the picture is filed under the shot's id
 * and the map row's count moves; a peeking todo card still only comes
 * forward — opening the camera is still the wrong door for a card the
 * surveyor only meant to bring forward, so that one keeps needing two taps.
 *
 * CONNECTED: `useSurvey()`, `useServices()`, `useSyncStatus()`.
 */

import { useState, type ReactNode } from 'react';

import type { PhotoRef, SectionInstance } from '../../core/types';
import { frontOn, initialCenter, nextCenter } from '../state/photoFan';
import { shelfModel, type ParkedInfo, type ShelfCard } from '../state/photoShelf';
import { useServices } from '../state/services';
import { useSurvey } from '../state/SurveyProvider';
import { useSyncStatus } from '../state/useSyncStatus';
import { guardedWrite } from '../state/writeGate';
import { useOriginal, useThumbnails } from './PhotoCapture';
import { PhotoFan, type FanCard } from './PhotoFan';
import { PhotoViewer } from './PhotoViewer';
import { usePhotoCapture, type PhotoScope } from './usePhotoCapture';
import { CameraGlyph } from './MapTreeView';

export interface PhotoShelfProps {
  section: SectionInstance;
}

type Chooser = { scope: PhotoScope; label: string } | null;

/** One card, one key — a shot keeps its key whether its photo is there or not. */
function cardKey(card: ShelfCard): string {
  if (card.kind === 'shot') return `shot:${card.shotListId}`;
  if (card.kind === 'extra') return `extra:${card.photo.id}`;
  // The placeholder is the one card a section ever has when it has none of
  // its own — there is never a second one to key against, so the literal
  // is stable across every render (round 7 task 5).
  return 'placeholder';
}

/**
 * The fronted card, remembered with the cards it was chosen among, so the
 * next render can tell what changed and where the front should go.
 */
interface Hand {
  signature: string;
  sectionId: string;
  keys: readonly string[];
  /** How many photos the cards carried — growth means a capture landed. */
  photoCount: number;
  front: string | undefined;
}

export function PhotoShelf({ section }: PhotoShelfProps): ReactNode {
  const { sync, store, log } = useServices();
  const { doc, results, surveyId, readOnly } = useSurvey();
  const status = useSyncStatus(sync);

  const parked = new Map<string, ParkedInfo>(
    status.attention
      .filter((entry) => entry.kind === 'photo' && entry.photoId !== undefined)
      .map((entry) => [
        entry.photoId as string,
        { reason: entry.reason, canRetry: entry.canRetry !== false },
      ]),
  );

  const shelf = shelfModel({ doc, results, section, parkedPhotoIds: parked });
  /*
   * Round 7 task 10, ruling 12: read-only hides the placeholder — the one
   * card `shelfModel` mints for a section holding neither a shot list nor a
   * photo — along with the add pill below. Both exist only to invite a
   * capture, and there is nowhere left on this survey to file one.
   */
  const cards = readOnly ? shelf.cards.filter((card) => card.kind !== 'placeholder') : shelf.cards;

  const photoRefs = cards.flatMap((card) => {
    if (card.kind === 'shot') return card.photo ? [card.photo] : [];
    if (card.kind === 'extra') return [card.photo];
    return []; // placeholder: never carries a photo (round 7 task 5).
  });
  const photoIds = photoRefs.map((photo) => photo.id);
  const thumbs = useThumbnails(photoRefs);

  const [chooser, setChooser] = useState<Chooser>(null);
  /*
   * The key of the card being viewed, NOT the card object — the object is a
   * snapshot of `photo.label` (and everything else) at the instant it was
   * tapped, and a label write while the viewer is open never touches this
   * state, so a stale snapshot would go on feeding the viewer its own OLD
   * label forever (fix round 1: reopening the label editor within one
   * session re-seeded the input from the tap-time snapshot and a second
   * keystroke overwrote the label just saved). Deriving `viewing` from the
   * live `shelf.cards` below means every render reads the CURRENT document —
   * the viewer's title, its aria-label and the editor's `defaultValue` are
   * never more than one render behind a write this component itself made.
   */
  const [viewingKey, setViewingKey] = useState<string | null>(null);

  /*
   * ── the front (D-032) ────────────────────────────────────────────────────
   * `pending` is the card the picker was last opened FOR — a shot's key, or
   * null for a section-level extra. A shot's card keeps its key when the
   * photo lands, so the key diff alone cannot see it change; this says which.
   * State rather than a ref: it is consumed during render, and a ref consumed
   * during render is consumed twice under StrictMode.
   */
  const keys = cards.map(cardKey);
  const signature = `${section.id}|${keys.join(',')}|${photoIds.join(',')}`;
  const [pending, setPending] = useState<string | null>(null);
  const [hand, setHand] = useState<Hand>(() => ({
    signature,
    sectionId: section.id,
    keys,
    photoCount: photoIds.length,
    front: keys[initialCenter(keys.length)],
  }));
  if (hand.signature !== signature) {
    // Deriving from the previous render — React re-renders before committing.
    const sameSection = hand.sectionId === section.id;
    const landed = sameSection && photoIds.length > hand.photoCount;
    const index = sameSection
      ? nextCenter(hand.keys, keys, Math.max(0, hand.keys.indexOf(hand.front ?? '')), landed ? (pending ?? undefined) : undefined)
      : initialCenter(keys.length);
    setHand({ signature, sectionId: section.id, keys, photoCount: photoIds.length, front: keys[index] });
    /*
     * ONLY when a picture landed (the count grew), never on a removal: Retake
     * deletes and then reopens the picker for the same shot, and the doc's
     * re-render for the deletion can arrive after `openFor` has set the new
     * scope — resetting it here would file the retaken shot as an extra.
     */
    if (landed) {
      if (pending !== null) setPending(null);
      if (chooser !== null) setChooser(null);
    }
  }

  const sectionScope: PhotoScope = { sectionInstanceId: section.id };
  const cam = usePhotoCapture(chooser?.scope ?? sectionScope);

  const cardPhoto = (card: ShelfCard): PhotoRef | undefined =>
    card.kind === 'placeholder' ? undefined : card.photo;

  // Re-derived every render from the LIVE `shelf.cards`, never cached: see
  // the note on `viewingKey` above. A key with no matching card (the photo
  // was deleted, on this device or another) simply closes the viewer.
  const viewing = viewingKey ? (cards.find((card) => cardKey(card) === viewingKey) ?? null) : null;
  const viewingPhoto = viewing ? cardPhoto(viewing) : undefined;
  /*
   * The cards draw thumbnails; the viewer is the one place that asks for the
   * original (round 8.1, task 4), and only while it is open on a photo. Above
   * the early return below, as every hook must be.
   */
  const original = useOriginal(viewingPhoto ?? null);

  /*
   * Fix round 1, minor 6: read-only's own filter above (`cards`) CAN empty
   * the hand — the one case is a section with neither a shot list nor a
   * photo, whose only card was the placeholder just filtered out. There is
   * nothing left to show: no add pill (also gone under `readOnly`), no
   * placeholder, no photo. Render nothing rather than a "Photos" heading
   * over an empty stage. Safe here, after every hook above has run.
   */
  if (readOnly && cards.length === 0) return null;

  /*
   * D-030 R10: one tap opens the OS's own picker. The scope state survives
   * from the chooser era so a todo card still files its pick against its shot
   * — the state is set, then the input is clicked; the change event fires
   * long after React has re-rendered the input's handler with the new scope.
   */
  const openFor = (scope: PhotoScope, label: string, replaces?: string): void => {
    setChooser({ scope, label });
    setPending(scope.shotListId ? `shot:${scope.shotListId}` : null);
    cam.open({ replaces });
  };

  /*
   * D-032 amendment, round 7 task 5: the design authority wants the hand on
   * EVERY survey screen — *"the only time you wouldn't see a picture
   * carousel is Building Setup & Room Setup which are just configuration
   * screens."* Those two never reach this component: Building has its own
   * screen with no `<PhotoShelf>`, and `SectionScreen` skips it for the
   * structure section (Room Setup) — see that file. Every OTHER section
   * that lands here always gets a shelf. `shelfModel` itself never hands back
   * an empty `cards` array — a section with no shot list and no extra gets
   * one `PlaceholderCard` reading "Photo 1" instead (D-032 amendment, this
   * round), rendered by the very branch below that already draws a
   * still-to-take shot as the camera glyph. Read-only's own filter (`cards`,
   * above) CAN empty it again by dropping that one placeholder — the early
   * return just above this comment is what that case gets, AFTER every hook
   * has already run.
   */

  const fanCards: FanCard[] = cards.map((card) => {
    const photo = cardPhoto(card);
    const key = cardKey(card);
    const title = card.title;
    const src = photo ? thumbs[photo.id] : undefined;
    const stuck = photo ? parked.get(photo.id) : undefined;
    if (!photo) {
      // A recommended shot still to take, OR the placeholder (round 7 task
      // 5) when the section has neither a shot nor an extra: either way the
      // card IS the camera. The placeholder's tap is an extra, never a shot
      // — there is no shot to file it under — so it opens the picker scoped
      // to the section itself, the same door the header's add pill uses.
      //
      // Fix round 1: the placeholder wears `shelf-card--todo` too — same
      // camera glyph, same look — but ALSO `shelf-card--placeholder`, a
      // second modifier and nothing more (no rule of its own in
      // theme.css), so a helper walking every "still to take" card can
      // tell "a recommended shot" from "the empty hand's one waiting
      // card" without reading its label. `walkthrough.spec.ts`'s
      // `takeRecommendedPhotos` learned this the hard way: it drove every
      // `.shelf-card--todo` through `takeShot`, including this one on Cab
      // Interior, then waited forever for a DONE card labelled "Photo 1"
      // — filing the photo correctly turns it into an EXTRA labelled
      // "Photo 1 Extra" instead, because it was never a shot.
      return {
        key,
        stamp: '',
        node: (
          <button
            type="button"
            className={
              card.kind === 'placeholder'
                ? 'shelf-card shelf-card--todo shelf-card--placeholder'
                : 'shelf-card shelf-card--todo'
            }
            aria-label={`Add photo — ${title}`}
            // Round 9.1 (D-044) item 1: the visible "Required" word is gone
            // from the card (§1), but `card.required` is still a real fact
            // the engine blocks on, so it stays on the DOM as a data
            // attribute — never read aloud, never printed, just addressable
            // (`tests/e2e/section-title.ts`'s `requiredShotsToTake` reads
            // this now, not the band's own text).
            data-required={card.kind === 'shot' && card.required ? 'true' : undefined}
            // Round 7 task 10, ruling 12: a recommended shot still to take
            // stays visible — it is still what the office got, a gap and
            // all — but read-only means it cannot be turned into a capture
            // any more.
            disabled={readOnly}
            onClick={() =>
              openFor(
                { sectionInstanceId: section.id, shotListId: card.kind === 'shot' ? card.shotListId : undefined },
                card.kind === 'placeholder' ? section.title : title,
              )
            }
          >
            <span className="shelf-card__ph" aria-hidden="true">
              <span className="shelf-card__cam">
                <CameraGlyph size={20} />
              </span>
            </span>
            <span className="shelf-card__band">
              <span className="shelf-card__label" data-legibility="display">
                {title}
              </span>
              {/* Amendment 8 (D-041); round 9.1 (D-044) item 1: the card no
               * longer prints the standalone word "Required" — Stephanie's
               * feedback, "the word takes up too much space" — so this line
               * appears only once the surveyor has written a reason for a
               * required shot still not taken. `card.required` itself is
               * untouched: the engine still blocks on the shot, and the last
               * look's row still exists (§4 rule 8 warns for a missing
               * recommended shot; this one blocks). */}
              {card.kind === 'shot' && card.required && card.reason ? (
                <span className="shelf-card__req" data-legibility="helper">
                  {card.reason}
                </span>
              ) : null}
            </span>
          </button>
        ),
      };
    }
    return {
      key,
      stamp: photo.id,
      // Round 8.1 task 5: a done card's tap already opens something legible —
      // its own viewer — so a tap on it while it only peeks fronts it AND
      // opens the viewer in the same tap (`PhotoFan`'s `onClickCapture` reads
      // this rather than swallowing the click, as it still does for a todo
      // card's camera).
      actsWhenPeeking: true,
      node: (
        <button
          type="button"
          className={[
            'shelf-card',
            'shelf-card--done',
            card.kind === 'extra' ? 'shelf-card--extra' : '',
            stuck ? 'shelf-card--stuck' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label={`${title}${stuck ? ' — Needs you' : ''}. Open`}
          data-parked={card.kind !== 'placeholder' && card.parked ? 'true' : undefined}
          onClick={() => setViewingKey(key)}
        >
          <span className="shelf-card__ph">
            {src ? (
              <img className="shelf-card__img" src={src} alt="" />
            ) : (
              <span className="shelf-card__img shelf-card__img--none" aria-hidden="true">
                ▣
              </span>
            )}
            {stuck ? null : (
              <span className="shelf-card__tick" aria-hidden="true">
                ✓
              </span>
            )}
          </span>
          <span className="shelf-card__band">
            <span className="shelf-card__label" data-legibility="display">
              {title}
            </span>
          </span>
        </button>
      ),
    };
  });

  return (
    <section className="shelf" aria-label={`Photos · ${section.title}`}>
      <div className="shelf__cap">
        <h2 className="eyebrow shelf__eyebrow" data-legibility="display">
          Photos
        </h2>
        {/* Round 7 task 10, ruling 12: no add pill on a read-only survey —
         * there is nowhere for a new capture to go. */}
        {readOnly ? null : (
          <button
            type="button"
            className="addpill"
            aria-label={`Add a photo — ${section.title}`}
            onClick={() => openFor(sectionScope, section.title)}
          >
            <CameraGlyph size={17} />
            <span aria-hidden="true">＋</span>
          </button>
        )}
      </div>

      {/* The one hidden input — scoped to whatever was last tapped. Nothing
       * on a read-only survey opens it any more (the add pill and the todo
       * cards are gone or disabled above), and `usePhotoCapture` refuses the
       * write if it is ever reached anyway — `disabled` here is the third
       * line, not the only one. */}
      <input {...cam.inputProps} disabled={readOnly} />

      {/* Above the buttons (D-011 item 6): what happened to the last pick. */}
      {cam.error ? (
        <p className="photos__error" role="alert">
          {cam.error}
        </p>
      ) : null}

      <PhotoFan
        cards={fanCards}
        front={hand.front}
        onFront={(key) => setHand((h) => ({ ...h, front: frontOn(h.keys, key, h.front) }))}
        label={`Photos · ${section.title}`}
      />

      {viewing && viewingPhoto ? (
        <PhotoViewer
          photo={viewingPhoto}
          label={viewing.title}
          extra={viewing.kind === 'extra'}
          note={viewing.kind === 'shot' ? viewing.note : undefined}
          src={thumbs[viewingPhoto.id]}
          original={original}
          parked={parked.get(viewingPhoto.id)}
          sectionTitle={section.title}
          readOnly={readOnly}
          onClose={() => setViewingKey(null)}
          onLabelChange={(next) => {
            // Minor 5 (round 7 final review): the same `guardedWrite` door
            // `usePhotoCapture`'s `capture()`/`remove()` use, not a bare
            // `if (readOnly) return` — `store.setPhotoLabel` bypasses the
            // provider's `write()` the same way `addPhoto`/`deletePhoto` do,
            // so it needs the same logged refusal, not a silent no-op.
            const photo = cardPhoto(viewing) as PhotoRef;
            void guardedWrite({ readOnly, surveyId, door: 'photo.label', log }, () =>
              store.setPhotoLabel(surveyId, photo.id, next),
            ).catch(() => undefined);
          }}
          onRetake={() => {
            const photo = cardPhoto(viewing) as PhotoRef;
            const scope: PhotoScope = {
              sectionInstanceId: section.id,
              ...(photo.shotListId ? { shotListId: photo.shotListId } : {}),
            };
            const label = viewing.kind === 'shot' ? viewing.label : section.title;
            setViewingKey(null);
            // Straight into the native picker (D-030 R10), SYNCHRONOUSLY in
            // the tap — no await first, or iOS never opens it (round 7 task
            // 2). The old photo is not touched here: `usePhotoCapture` deletes
            // it only once the new one lands, so cancelling the OS sheet
            // leaves the original exactly where it was.
            openFor(scope, label, photo.id);
          }}
          onDelete={() => {
            const photo = cardPhoto(viewing) as PhotoRef;
            setViewingKey(null);
            void cam.remove(photo.id);
          }}
          onRetry={() => {
            const photo = cardPhoto(viewing) as PhotoRef;
            void cam.retry(photo.id);
          }}
        />
      ) : null}
    </section>
  );
}
